// client/src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ChakraProvider, extendTheme, Box, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription } from "@chakra-ui/react";
import Dashboard from "./Dashboard";
import Home from "./Home";
import Navbar from "./Components/Navbar";
import { AuthProvider, useAuth } from "./context/AuthContext";

const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
  fonts: {
    heading: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif`,
  },
  styles: {
    global: (props) => ({
      body: {
        bg: props.colorMode === "dark" ? "rgb(27 28 35)" : "#f0e7db",
        color: props.colorMode === "dark" ? "white" : "gray.800",
        fontFeatureSettings: "'ss01' on, 'ss02' on, 'cv01' on, 'cv03' on",
      },
    }),
  },
  components: {
    Button: {
      baseStyle: (props) => ({
        bg: props.colorMode === "dark" ? "green.400" : "green.500",
        color: "white",
        _hover: {
          bg: props.colorMode === "dark" ? "green.500" : "green.600",
        },
        fontWeight: "medium",
        letterSpacing: "tight",
      }),
    },
    Input: {
      baseStyle: (props) => ({
        field: {
          bg: props.colorMode === "dark" ? "gray.700" : "rgba(255, 255, 255, 0.8)",
          color: props.colorMode === "dark" ? "white" : "gray.800",
          _hover: {
            borderColor: props.colorMode === "dark" ? "gray.500" : "gray.400",
          },
          _focus: {
            borderColor: "green.400",
          },
          fontWeight: "medium",
          letterSpacing: "tight",
        },
      }),
    },
    Checkbox: {
      baseStyle: (props) => ({
        control: {
          borderColor: props.colorMode === "dark" ? "gray.600" : "gray.400",
        },
        label: {
          fontWeight: "medium",
          letterSpacing: "tight",
        },
      }),
    },
    NumberInput: {
      baseStyle: (props) => ({
        field: {
          bg: props.colorMode === "dark" ? "gray.700" : "rgba(255, 255, 255, 0.8)",
          color: props.colorMode === "dark" ? "white" : "gray.800",
          fontWeight: "medium",
          letterSpacing: "tight",
        },
      }),
    },
    Text: {
      baseStyle: {
        fontWeight: "medium",
        letterSpacing: "tight",
      },
    },
    Heading: {
      baseStyle: {
        fontWeight: "extrabold",
        letterSpacing: "tight",
      },
    },
  },
  colors: {
    gray: {
      50: "#F7FAFC",
      100: "#EDF2F7",
      200: "#E2E8F0",
      300: "#CBD5E0",
      400: "#A0AEC0",
      500: "#718096",
      600: "#4A5568",
      700: "#2D3748",
      800: "#1A202C",
      900: "#171923",
    },
  },
});

function AppContent() {
  const { isLoading, error } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" color="green.400" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Router>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
